import React, { useState, useEffect, useRef } from 'react';
import { useSetLastSeenMutation } from '@/store/api';
import type { User } from '@/types/layout.types';

interface UseHiatusDetectionReturn {
	showHiatusModal: boolean;
	setShowHiatusModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useHiatusDetection(
	user: User | null | undefined,
	notificationsReady: boolean,
	notifyOnce: (key: string, email: string | undefined, title: string, message: string) => void
): UseHiatusDetectionReturn {
	const [showHiatusModal, setShowHiatusModal] = useState(false);
	const [setLastSeen] = useSetLastSeenMutation();
	const processedDateRef = useRef<string | null>(null);

	useEffect(() => {
		if (!user?.last_seen_date || !notificationsReady) return;
		if (processedDateRef.current === user.last_seen_date) return;

		processedDateRef.current = user.last_seen_date;

		const lastSeen = new Date(user.last_seen_date).getTime();
		const now = Date.now();
		const diffInDays = (now - lastSeen) / (1000 * 60 * 60 * 24);

		const updateLastSeen = async () => {
			try {
				if (diffInDays > 7) {
					// defer state update to avoid synchronous setState inside effect
					setTimeout(() => setShowHiatusModal(true), 0);
					await setLastSeen(undefined).unwrap();
				} else if (diffInDays > 1) {
					await setLastSeen(undefined).unwrap();
				}
			} catch (err) {
				console.warn('Set last seen date failed', err);
			}
		};

		updateLastSeen();
	}, [user?.last_seen_date, notificationsReady, setLastSeen]);

	useEffect(() => {
		if (showHiatusModal && user?.email) {
			notifyOnce('hiatus', user?.email, 'Welcome Back', "Look who's back! The markets missed you.");
		}
	}, [showHiatusModal, user?.email, notifyOnce]);

	return { showHiatusModal, setShowHiatusModal };
}
